import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import * as eventActions from 'Actions/events';
import { Box, useMediaQuery } from '@mui/material';
import Logo from 'Components/Logo';
import { useDispatch, useSelector } from 'react-redux';
import { ALLOWED_PATHS_ANY_PLATFORM, isCapacitorNative } from 'Utils/Constants';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { AppDispatch } from 'Reducers';
import { detectDevice } from 'Utils';
import {
  isCompatibleAndroid,
  isMobileBrowser,
  isSafariBrowser,
  isFirefoxBrowser,
} from 'Utils/UserAgent';
import { useLocation, useNavigate } from 'react-router';
import { MUSEFLOW_OFF_WHITE_1 } from 'Components/Colors';
import LogOutButton from 'Components/LogOutButton';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { MainAppReducer } from 'Types';

const IPAD_APP_STORE_URL = 'https://apps.apple.com/us/app/museflow/id6743666055';

const AUTH_AND_ONBOARDING_PATHS = ['/signin', '/register', '/welcome', '/verify'];

const isAuthOrOnboardingPath = (pathname: string): boolean =>
  AUTH_AND_ONBOARDING_PATHS.some((path) => pathname.startsWith(path));

// Show note that they need to open their browser that is not safari!
const DeviceCompatibilityBlockingScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const auth = useAuthWrapperContext();
  const { userDataFetched } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const [showSafariBlockingScreen, setShowSafariBlockingScreen] = useState(false);
  const [showMobileBlockingScreen, setShowMobileBlockingScreen] = useState(false);
  // TODO(MUS-278): temporary Firefox block while bugs are fixed.
  const [showFirefoxBlockingScreen, setShowFirefoxBlockingScreen] = useState(false);
  const matchesHeight = useMediaQuery('(min-width:1000px)');
  const matchesWidth = useMediaQuery('(min-height:1000px)');
  const mobileSightReadingEnabled = useFeatureFlag(FeatureFlag.USE_MOBILE_SIGHT_READING);

  // Don't show blocking screens while auth is still loading or user data hasn't been fetched yet
  const authInProgress =
    auth.isLoading ||
    auth.oauthPending ||
    auth.processPending ||
    (auth.isAuthenticated && !userDataFetched);

  useEffect(() => {
    if (isAuthOrOnboardingPath(pathname) || authInProgress) {
      setShowMobileBlockingScreen(false);
      setShowSafariBlockingScreen(false);
      // TODO(MUS-278): temporary Firefox block while bugs are fixed.
      setShowFirefoxBlockingScreen(false);
      return;
    }

    // Check paths that allow on any browser
    if (!ALLOWED_PATHS_ANY_PLATFORM.includes(pathname)) {
      const isIosMobileWeb =
        !isCapacitorNative() &&
        (detectDevice() == 'ipad' || detectDevice() == 'iphone') &&
        !pathname.includes('verify');
      const shouldBlockMobileWeb = isIosMobileWeb && !mobileSightReadingEnabled;

      if (shouldBlockMobileWeb) {
        console.log('Mobile browser detected, redirecting to roadmap');
        setShowMobileBlockingScreen(true);
      } else {
        setShowMobileBlockingScreen(false);
      }
      console.log(
        'isSafariBrowser()',
        isSafariBrowser(),
        "&& !pathname.includes('verify')",
        !pathname.includes('verify'),
      );
      if (
        !isCapacitorNative() &&
        isSafariBrowser() &&
        detectDevice() == 'web' &&
        !pathname.includes('verify')
      ) {
        // return <SafariBlockingScreen />;
        setShowSafariBlockingScreen(true);
        console.log('safari browser detected, redirecting to roadmap');
        // navigate('/roadmap')
      }

      // TODO(MUS-278): temporary Firefox block while bugs are fixed.
      // Mirrors the Safari block above; remove this whole `if` (and the related
      // Firefox state/effect/modal branch) once Firefox is supported again.
      if (
        !isCapacitorNative() &&
        isFirefoxBrowser() &&
        detectDevice() == 'web' &&
        !pathname.includes('verify')
      ) {
        setShowFirefoxBlockingScreen(true);
        console.log('firefox browser detected, showing blocking screen');
      } else {
        setShowFirefoxBlockingScreen(false);
      }
    } else {
      setShowMobileBlockingScreen(false);
      setShowSafariBlockingScreen(false);
      // TODO(MUS-278): temporary Firefox block while bugs are fixed.
      setShowFirefoxBlockingScreen(false);
    }
  }, [pathname, authInProgress, mobileSightReadingEnabled]);

  useEffect(() => {
    if (showSafariBlockingScreen) {
      dispatch(eventActions.safariBlockedEventAction());
    }
  }, [showSafariBlockingScreen]);

  // TODO(MUS-278): temporary Firefox block while bugs are fixed.
  useEffect(() => {
    if (showFirefoxBlockingScreen) {
      dispatch(eventActions.firefoxBlockedEventAction());
    }
  }, [showFirefoxBlockingScreen]);

  return (
    <>
      {showSafariBlockingScreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.80)',
            zIndex: 3000,
          }}
        >
          <CompatibilityBlockingModal blockingScreen="safari" />
          <Box
            sx={{
              position: 'absolute',
              bottom: 'calc(8vh + env(safe-area-inset-bottom, 0px))',
              left: '20px',
            }}
          >
            <LogOutButton sx={{}} />
          </Box>
        </Box>
      )}

      {showMobileBlockingScreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.80)',
            zIndex: 3000,
          }}
        >
          <CompatibilityBlockingModal blockingScreen="mobile" />
          <Box
            sx={{
              position: 'absolute',
              bottom: 'calc(8vh + env(safe-area-inset-bottom, 0px))',
              left: '20px',
            }}
          >
            <LogOutButton sx={{}} />
          </Box>
        </Box>
      )}

      {/* TODO(MUS-278): temporary Firefox block while bugs are fixed. Remove this block once Firefox is supported. */}
      {showFirefoxBlockingScreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.80)',
            zIndex: 3000,
          }}
        >
          <CompatibilityBlockingModal blockingScreen="firefox" />
          <Box
            sx={{
              position: 'absolute',
              bottom: 'calc(8vh + env(safe-area-inset-bottom, 0px))',
              left: '20px',
            }}
          >
            <LogOutButton sx={{}} />
          </Box>
        </Box>
      )}
    </>
  );
};

interface BlockingModalProps {
  blockingScreen: string;
}

const CompatibilityBlockingModal: FC<BlockingModalProps> = ({ blockingScreen }) => {
  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            textAlign: 'center',
          }}
        >
          {blockingScreen === 'safari' && (
            <>
              <Box>
                <Logo absolute={false} href="https://www.museflow.ai" gradient={false} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    lineHeight: '145%',
                    color: MUSEFLOW_OFF_WHITE_1,
                    maxWidth: '400px',
                    marginTop: '-40px',
                    px: 2,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  To continue on the web, use Chrome or Edge
                </Box>
              </Box>
            </>
          )}

          {/* TODO(MUS-278): temporary Firefox block while bugs are fixed. Remove this branch once Firefox is supported. */}
          {blockingScreen === 'firefox' && (
            <>
              <Box>
                <Logo absolute={false} href="https://www.museflow.ai" gradient={false} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    lineHeight: '145%',
                    color: MUSEFLOW_OFF_WHITE_1,
                    maxWidth: '400px',
                    marginTop: '-40px',
                    px: 2,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  Firefox isn&apos;t fully supported yet. To continue on the web, use Chrome or Edge
                </Box>
              </Box>
            </>
          )}

          {blockingScreen === 'mobile' && (
            <>
              <Box>
                <Logo absolute={false} href="museflow://" gradient={false} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: MUSEFLOW_OFF_WHITE_1,
                    maxWidth: '400px',
                    marginTop: '-40px',
                    px: 2,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  MuseFlow is best experienced on the iPad app, or on a computer using Chrome or
                  Edge
                </Box>
                <Box
                  component="a"
                  href={IPAD_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-block',
                    marginTop: '16px',
                    color: '#4A90D9',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  Download MuseFlow on iPad
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export { DeviceCompatibilityBlockingScreen };
